/**
 * Vault encryption (spec §6.4): vault password → scrypt → 256-bit key → AES-256-GCM.
 *
 * - scrypt (not argon2id) because it runs fast enough in pure JS in every browser, the extension
 *   and the mobile web view; N = 2^16, r = 8 uses 64 MiB, the same memory cost as the signer's
 *   argon2id (D22).
 * - A fresh random salt and nonce on EVERY save, so the same vault never encrypts the same way twice.
 * - "Additional data" (aad) binds the blob to its network and kind: a testnet blob can't be
 *   swapped in as a mainnet vault without the decryption failing.
 * - A wrong password and a tampered blob look the same: WrongPasswordError.
 */
import { gcm } from "@noble/ciphers/aes.js";
import { randomBytes } from "@noble/ciphers/utils.js";
import { scryptAsync } from "@noble/hashes/scrypt.js";
import { base64 } from "@scure/base";
export const DEFAULT_KDF = { N: 2 ** 16, r: 8, p: 1 };
/** Weakest parameters ever accepted when decrypting (stops a tampered blob from making unlock trivial). */
const MIN_N = 2 ** 10;
const MAX_N = 2 ** 20;
export class WrongPasswordError extends Error {
    constructor() {
        super("Wrong vault password");
        this.name = "WrongPasswordError";
    }
}
const encoder = new TextEncoder();
function passwordBytes(password) {
    // NFKC so the same password typed on different keyboards/platforms gives the same bytes.
    return encoder.encode(password.normalize("NFKC"));
}
async function deriveKey(password, kdf) {
    const pw = passwordBytes(password);
    try {
        return await scryptAsync(pw, base64.decode(kdf.salt), {
            N: kdf.N,
            r: kdf.r,
            p: kdf.p,
            dkLen: 32,
            maxmem: 256 * 1024 * 1024,
        });
    }
    finally {
        pw.fill(0);
    }
}
export async function encryptBytes(plaintext, password, aad, params = DEFAULT_KDF) {
    const kdf = { name: "scrypt", ...params, salt: base64.encode(randomBytes(16)) };
    const key = await deriveKey(password, kdf);
    const nonce = randomBytes(12);
    try {
        const data = gcm(key, nonce, encoder.encode(aad)).encrypt(plaintext);
        return { v: 1, kdf, cipher: "aes-256-gcm", nonce: base64.encode(nonce), data: base64.encode(data) };
    }
    finally {
        key.fill(0);
    }
}
export async function decryptBytes(blob, password, aad) {
    if (blob.v !== 1 || blob.cipher !== "aes-256-gcm" || blob.kdf.name !== "scrypt") {
        throw new Error("Unknown vault format");
    }
    const { N, r, p } = blob.kdf;
    if (!Number.isInteger(N) || N < MIN_N || N > MAX_N || (N & (N - 1)) !== 0 || r < 1 || r > 16 || p !== 1) {
        throw new Error("Unsupported vault encryption settings");
    }
    const key = await deriveKey(password, blob.kdf);
    try {
        return gcm(key, base64.decode(blob.nonce), encoder.encode(aad)).decrypt(base64.decode(blob.data));
    }
    catch {
        throw new WrongPasswordError();
    }
    finally {
        key.fill(0);
    }
}
//# sourceMappingURL=crypto.js.map